import cache from "@app/js/modules/cache";
import { Infos, Paths } from "@app/js/modules/connectionsDrawer";
import {
  calculateTotalCost,
  emptyState,
} from "@app/js/modules/organigrama/helpers";
import Axios from "axios";
import { isString, orderBy } from "lodash";
import Swal from "sweetalert2";
import { GetterTree, MutationTree } from "vuex";
import { AppResponse } from "../../../modules/ajax";
import app from "../../../modules/app";
import { IMusico, Models } from "../../../types";
import {
  CostResponse,
  IResponse,
  Organigrama,
  OrganigramaActions,
  OrganigramaAssignation,
  OrganigramaError,
  OrganigramaState,
  PrecioMusicoFecha,
  PrecioVehiculoEmpresaFecha,
} from "./../../../types/store/organigrama";

const setCustomProcessResponse = (context) => {
  app.customProcessResponse = (response) => {
    if (response.errors[0] === "Lo sentimos, el organigrama está bloqueado") {
      Swal.fire({
        title: "El organigrama está bloqueado",
        text: "¿Quieres desbloquearlo?",
        cancelButtonText: "No",
        confirmButtonText: "Sí",
        showCancelButton: true,
        icon: "question",
      }).then((result) => {
        if (result.isConfirmed) {
          context.dispatch("unblock");
        }
      });

      return null;
    }

    return response;
  };
};

const api = {
  urls: {
    servicios: "/organigrama/index",
    connections: "/organigrama/connections",
    status: "/organigrama/status",
    block: "/organigrama/block",
    unblock: "/organigrama/unblock",
    costos: "/organigrama/musico/costs",
    musico: {
      add: "/organigrama/servicio/musico/add",
      remove: "/organigrama/servicio/musico/remove",
      update: "/organigrama/servicio/musico/update",
    },
    select: {
      musico: "/organigrama/musico/select",
      vehiculo: "/organigrama/vehiculo/select",
      mode: "/organigrama/change/mode",
    },
  },
};

export const state = (): OrganigramaState => emptyState;

const getters: GetterTree<OrganigramaState, unknown> = {
  serviciosFiltered(state) {
    return state.servicios.map((servicio) => {
      return {
        ...servicio,
        dimmed: state.filteredIds !== null &&
          !state.filteredIds.includes(servicio.id),
      };
    });
  },

  serviciosOk(state) {
    return state.servicios.filter((servicio) => servicio.hasIntegrantesOk);
  },

  assignedMusicos(state) {
    const bag: OrganigramaAssignation[] = [];

    state.servicios.forEach((servicio) => {
      servicio.integrantes.forEach((musico) => {
        bag.push({
          musico,
          servicio,
        });
      });
    });

    return bag;
  },

  explainCostos(state): Organigrama.ExplainCost[] {
    const dataParticular = state.cost.costoMusicoFecha.map((el) => {
      return {
        nombre: el.musico.nombreCompleto,
        costoDesplazamientos: el.costoDesplazamientos,
        costoServicios: el.costoServicios,
        costoTotal: el.costoDesplazamientos + el.costoServicios,
      };
    });

    const dataEmpresa = state.cost.vehiculosEmpresa.map((el) => {
      return {
        nombre: el.vehiculo.nombre,
        costoDesplazamientos: el.costoDesplazamientos,
        costoServicios: 0,
        costoTotal: el.costoDesplazamientos,
      };
    });

    const data = dataParticular.concat(dataEmpresa);
    return orderBy(data, ["costoTotal", "nombre"], ["desc", "asc"]);
  },
};

const actions: OrganigramaActions = {
  fetchServicios(
    _context,
    payload: { idMusico?: number },
  ): Promise<AppResponse<{ servicios: Models.Servicio[] }>> {
    return app.post<{ servicios: Models.Servicio[] }>(api.urls.servicios, {
      id_musico: payload?.idMusico,
    });
  },

  async getServicios(context, payload: { idMusico?: number }): Promise<void> {
    const response = await context.dispatch("fetchServicios", payload);
    if (response.data) {
      context.dispatch("setServicios", response.data.servicios);
    }
  },

  async fetchConnections(context): Promise<void> {
    const response = await app.get<{ infos: Infos; paths: Paths }>(
      api.urls.connections,
    );
    if (response.data) {
      context.dispatch("setConnections", response.data.infos);
      context.commit("SET_PATHS", response.data.paths);
    }
  },

  setServicios(context, payload: Models.Servicio[]): void {
    context.commit("SET_SERVICIOS", payload);
  },

  setServicio(context, payload: Models.Servicio): void {
    context.commit("SET_SERVICIO", payload);
  },

  setConnections(context, payload: Infos): void {
    context.commit("SET_CONNECTIONS", payload);
  },

  updateTotalCost(context) {
    const totalCost = calculateTotalCost(context.state);
    context.commit("SET_TOTAL_COST", totalCost);
    context.commit("SET_ERRORS", totalCost.errors);
  },

  async fetchBlockStatus(context) {
    app
      .get<{ blocked: boolean; who: { nombreCompleto: string } }>(
        api.urls.status,
      )
      .then((response) => {
        if (response.data) {
          context.commit("SET_BLOCKED_STATUS", {
            blocked: response.data.blocked,
            blockedBy: response.data.who.nombreCompleto,
          });
        }
      });
  },

  block(context) {
    app.get(api.urls.block).then(() => {
      context.dispatch("fetchBlockStatus");
    });
  },

  unblock(context) {
    app.get(api.urls.unblock).then(() => {
      context.dispatch("fetchBlockStatus");
    });
  },

  async fetchCosto(context, musico: IMusico | null) {
    if (context.state.servicios.length && musico !== null) {
      context.commit("SET_FETCHING_STATUS", { key: "costs", value: true });
      const response = await app.post<CostResponse>(api.urls.costos, {
        fecha: context.state.servicios[0].fecha,
        id_musico: musico.id,
      });

      if (response.data) {
        response.data.costs.particular.forEach((el) =>
          context.commit("SET_MUSICO_COST", el)
        );
        response.data.costs.empresa.forEach((el) =>
          context.commit("SET_VEHICULO_EMPRESA_COST", el)
        );
        await context.dispatch("updateTotalCost");
        context.commit("SET_FETCHING_STATUS", { key: "costs", value: false });
      }
    }
  },

  async fetchCostos(context) {
    if (context.state.servicios.length) {
      context.commit("SET_FETCHING_STATUS", { key: "costs", value: true });

      const response = await app.get<CostResponse>(api.urls.costos, {
        fecha: context.state.servicios[0].fecha,
      });

      context.commit("SET_MUSICOS_COSTS", response.data);
      await context.dispatch("updateTotalCost");
      context.commit("SET_FETCHING_STATUS", { key: "costs", value: false });
    }
  },

  selectVehiculo(context, payload: { idVehiculo: number }) {
    return app.post(api.urls.select.vehiculo, {
      id_vehiculo: payload.idVehiculo,
    });
  },

  selectMusico(
    context,
    payload: { musico: Models.Musico; instrumento: Models.Instrumento },
  ) {
    context.commit("SET_SELECTED_MUSICO", payload);
    cache.set("organigrama.selected", payload);
  },

  async selectMode(context, payload: Organigrama.Mode) {
    const result = await app.post<{ mode: string }>(api.urls.select.mode, {
      mode: payload,
    });
    if (result.data) {
      context.commit("SET_MODE", result.data.mode);
    }
  },

  selectServicio(context, payload: { idServicio: number; checked: boolean }) {
    context.commit("SET_SELECTED_SERVICIO", payload);
  },

  async vehiculoUsage(context, payload: { idVehiculo: number }) {
    return Axios.get(
      `/organigrama/vehiculo/get/${payload.idVehiculo}/musicos`,
    ).then((response) => response.data);
  },

  async update(
    context,
    payload: { response: AppResponse<IResponse>; idMusico?: number },
  ): Promise<void> {
    if (payload.response.data && payload.response.data.servicio !== undefined) {
      context.dispatch("setServicio", payload.response.data.servicio);
      const response = (await context.dispatch("fetchServicios", {
        idMusico: payload.idMusico,
      })) as AppResponse<{ servicios: Models.Servicio[] }>;
      response.data?.servicios.forEach((servicio) => {
        context.dispatch("setServicio", servicio);
      });
    }
  },

  async addMusicoToServicio(
    context,
    payload: {
      servicio: Models.Servicio;
      vehiculo: Models.Vehiculo | string;
      musico?: Models.Musico;
      instrumento?: Models.Instrumento;
      context?: any;
    },
  ) {
    setCustomProcessResponse(context);

    const idVehiculo = isString(payload.vehiculo)
      ? payload.vehiculo
      : payload.vehiculo.id;

    const idMusico = payload.musico
      ? payload.musico.id
      : context.state.selected.musico?.id;

    const idInstrumento = payload.instrumento
      ? payload.instrumento.id
      : context.state.selected.instrumento?.id;

    const response = await app.post<IResponse>(api.urls.musico.add, {
      id_musico: idMusico,
      id_instrumento: idInstrumento,
      n_fax: payload.servicio.id,
      id_vehiculo: idVehiculo,
      context: payload.context,
    });

    context.dispatch("update", { response, idMusico });
    context.dispatch("fetchCosto", context.state.selected.musico);
  },

  async removeMusicoFromServicio(
    context,
    payload: { musico: IMusico; servicio: Models.Servicio },
  ) {
    setCustomProcessResponse(context);

    const response = await app.post<IResponse>(api.urls.musico.remove, {
      id_musico: payload.musico.id,
      n_fax: payload.servicio.id,
    });

    context.dispatch("update", { response, idMusico: payload.musico.id });
    context.dispatch("fetchCosto", payload.musico);

    context.commit("SET_FILTERED_IDS", null);
  },

  async updateMusicoFromServicio(
    context,
    payload: {
      musico: IMusico;
      servicio: Models.Servicio;
      vehiculo: Models.Vehiculo;
    },
  ) {
    setCustomProcessResponse(context);

    const idVehiculo = isString(payload.vehiculo)
      ? payload.vehiculo
      : payload.vehiculo.id;

    const response = await app.post<IResponse>(api.urls.musico.update, {
      id_musico: payload.musico.id,
      n_fax: payload.servicio.id,
      id_vehiculo: idVehiculo,
    });

    context.dispatch("update", { response, idMusico: payload.musico.id });
    context.dispatch("fetchCosto", payload.musico);
  },

  sendOradoresInfo(context, servicio: Models.Servicio) {
    return app.get(`/organigrama/servicio/${servicio.id}/info/oradores/send`);
  },

  sendOradoresInfoWhatsApp(context, servicio: Models.Servicio) {
    return app.get(
      `/organigrama/servicio/${servicio.id}/info/oradores/send/whatsapp`,
    );
  },

  servicioLockToggle(context, servicio: Models.Servicio) {
    return app.post(`/organigrama/servicio/${servicio.id}/update`, {
      locked: !servicio.locked,
    });
  },

  async servicioTooltip(context, servicio: Models.Servicio) {
    const response = await Axios.get(
      `/organigrama/servicio/${servicio.id}/tooltip`,
      {
        headers: {
          accept: "text/partial+html",
        },
      },
    );
    return response.data;
  },
};

const mutations: MutationTree<OrganigramaState> = {
  SET_SERVICIOS(state, servicios: Models.Servicio[]): void {
    state.servicios = servicios;
  },

  SET_SERVICIO(state, servicio: Models.Servicio): void {
    state.servicios = state.servicios.map((ser) => {
      if (ser.id === servicio.id) {
        return servicio;
      }

      return ser;
    });
  },

  SET_FILTERED_IDS(state, payload: number[] | null): void {
    state.filteredIds = payload;
  },

  SET_SELECTED_SERVICIO(
    state: OrganigramaState,
    payload: { id: number; selected: boolean },
  ): void {
    if (payload.selected && !state.selectedIds.includes(payload.id)) {
      state.selectedIds.push(payload.id);
    } else {
      state.selectedIds = state.selectedIds.filter((id) => id !== payload.id);
    }
  },

  UNSELECT_ALL_SERVICIOS(state): void {
    state.selectedIds = [];
  },

  SET_CONNECTIONS(state, connections: Infos): void {
    state.connections = connections;
  },

  SET_PATHS(state, paths: Paths): void {
    state.paths = paths;
  },

  SET_TOTAL_COST(state, payload: Organigrama.TotalCost) {
    state.totalCost = payload;
  },

  SET_MODE(state, mode: Organigrama.Mode) {
    state.mode = mode;
  },

  SET_ERRORS(state, errors: OrganigramaError) {
    state.errors = errors;
  },

  SET_MUSICOS_COSTS(state, response: CostResponse) {
    state.cost.costoMusicoFecha = response.costs.particular;
    state.cost.vehiculosEmpresa = response.costs.empresa;
  },

  SET_MUSICO_COST(state, cost: PrecioMusicoFecha) {
    const item = state.cost.costoMusicoFecha.find(
      (el) => el.musico.id === cost.musico.id,
    );
    if (item !== undefined) {
      item.costoDesplazamientos = cost.costoDesplazamientos;
      item.costoServicios = cost.costoServicios;
    } else {
      state.cost.costoMusicoFecha.push(cost);
    }
  },

  SET_VEHICULO_EMPRESA_COST(state, cost: PrecioVehiculoEmpresaFecha) {
    const item = state.cost.vehiculosEmpresa.find(
      (el) => el.vehiculo.id === cost.vehiculo.id,
    );
    if (item !== undefined) {
      item.costoDesplazamientos = cost.costoDesplazamientos;
    } else {
      state.cost.vehiculosEmpresa.push(cost);
    }
  },

  SET_FETCHING_STATUS(state, payload: { key: string; value: boolean }) {
    state.fetching[payload.key] = payload.value;
  },

  SET_BLOCKED_STATUS(state, payload: { blocked: boolean; blockedBy: string }) {
    state.blocked = payload.blocked;
    state.blockedBy = payload.blockedBy;
  },

  SET_FOCUSED_SERVICIO(state, payload: number | undefined) {
    state.focusedId = payload;
  },

  SET_SELECTED_MUSICO(
    state,
    payload: { musico: Models.Musico; instrumento: Models.Instrumento },
  ) {
    state.selected.instrumento = payload.instrumento;
    state.selected.musico = payload.musico;
  },

  SET_SORTER(state, payload: string) {
    state.sorter = payload;
  },

  TOGGLE_MUSICOS_LIST(state) {
    state.musicosList.open = !state.musicosList.open;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
